import { AfterContentChecked, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import {
  BatchAuftragCreateOptionen,
  BatchAuftragCreateOptionenMeta,
  BatchAuftragCreateRequest,
  BatchAuftragDetailEntryMeta,
  BatchAuftragService
} from '../../../../../core/batch-auftrag-api';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { markFormAsTouched } from '../../../../../shared/functions/form.functions';
import { trimToNull } from '../../../../../shared/functions/meta.functions';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuftragExportTemplate } from '../select-auftragstyp/select-auftragstyp.component';
import { ResolverValidationExceptionHandler } from '../../../../../core/extension/exceptions/exception-handler-rule';
import { CapAlertService } from '../../../../../core/extension/exceptions/cap-alert.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormatConfigService } from '../../../../../core/services/format-config.service';
import { CapFormColumnsDirective } from '../../../../../shared/form-control/form-columns.directive';
import { MetaDirective } from '../../../../../shared/directives/meta.directive';
import { CapFormControlComponent } from '../../../../../shared/form-control/form-control.component';
import { DatepickerComponent } from '../../../../../shared/datepicker/datepicker.component';
import { AutofocusDirective } from '../../../../../shared/directives/autofocus.directive';
import { MinDateDirective } from '../../../shared/validators/min-date.directive';
import { MetaDisablerDirective } from '../../../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../../../shared/directives/meta-validator.directive';
import { CdkStepperNext, CdkStepperPrevious } from '@angular/cdk/stepper';
import PeriodizitaetEnum = BatchAuftragCreateOptionen.PeriodizitaetEnum;
import PrioritaetEnum = BatchAuftragCreateOptionen.PrioritaetEnum;
import FormatEnum = BatchAuftragCreateOptionen.FormatEnum;

@Component({
  selector: 'cap-select-auftragsoptionen',
  templateUrl: './select-auftragsoptionen.component.html',
  styleUrls: ['./select-auftragsoptionen.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    MetaDirective,
    CapFormControlComponent,
    DatepickerComponent,
    AutofocusDirective,
    MinDateDirective,
    MetaDisablerDirective,
    MetaValidatorDirective,
    CdkStepperPrevious,
    RouterLink,
    CdkStepperNext,
    TranslatePipe
  ]
})
export class SelectAuftragsoptionenComponent implements OnInit, OnDestroy, AfterContentChecked {
  subscriptions = new Array<Subscription>();
  model: BatchAuftragCreateOptionenMeta;

  periodizitaetValues;
  prioritaetValues;
  formatValues;
  minDate;

  saveRequestRunning = false;

  @ViewChild('form', { static: true })
  form: NgForm;

  @Input()
  auftrag: BatchAuftragDetailEntryMeta;

  @Input()
  readonly: boolean;

  _template: AuftragExportTemplate;

  periodischeAuftraege: boolean = false;

  constructor(
    private batchAuftragService: BatchAuftragService,
    private datePipe: DatePipe,
    private capAlertService: CapAlertService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private resolverValidationExceptionHandler: ResolverValidationExceptionHandler,
    private formatService: FormatConfigService
  ) {
    keysToTranslate<string>([
      'datenexport.ausfuehrungsdatum',
      'datenexport.auftrag',
      'datenexport.auftragstitelUndBeschreibung',
      'datenexport.beschreibung',
      'datenexport.detailsZumAuftraggeber',
      'datenexport.email1',
      'datenexport.email2',
      'datenexport.email3',
      'datenexport.format',
      'datenexport.informationAnFolgendeEmpfaenger',
      'datenexport.periodizitaet',
      'datenexport.prioritaet',
      'datenexport.periodizitaetValues',
      'datenexport.prioritaetValues',
      'datenexport.titel',
      'EINMALIG',
      'MONATLICH',
      'WOECHENTLICH',
      'DRINGEND',
      'XML',
      'CSV',
      'XML_HTML'
    ]);
    this.periodizitaetValues = Object.values(PeriodizitaetEnum).sort();
    this.prioritaetValues = Object.values(PrioritaetEnum).sort();
    this.formatValues = Object.values(FormatEnum).sort();
    this.minDate = datePipe.transform(Date.now(), 'yyyy-MM-dd', '', 'de-CH');
  }

  get template(): AuftragExportTemplate {
    return this._template;
  }

  @Input()
  set template(template: AuftragExportTemplate) {
    if (!template) {
      return;
    }
    this._template = template;
  }

  ngOnInit() {
    if (this.readonly) {
      this.model = this.auftrag.optionen;
      return;
    }
    this.subscriptions.push(
      this.batchAuftragService.searchAuftragExportOptionenTemplate().subscribe((val) => {
        this.model = val;
      }),
      this.formatService.parameterConfig$.subscribe((config) => {
        this.periodischeAuftraege = !!config.parameters.DATENEXPORT_PERIODISCHE_AUFTRAEGE;
      })
    );
  }

  ngAfterContentChecked(): void {
    if (this.readonly) {
      this.form.form.disable();
    }
  }

  forceMinDate() {
    if (Date.parse(this.model.ausfuehrungsdatum).valueOf() < Date.parse(this.minDate).valueOf()) {
      this.model.ausfuehrungsdatum = this.minDate;
    }
  }

  onSave() {
    this.disableSaveButton();
    markFormAsTouched(this.form);

    if (this.form.valid) {
      // email2 und email3 sind nicht obligatorisch, d.h. ein leerer String ist erlaubt.
      // Wegen der Regexp-Validierung im Backend muss dieser aber auf null gesetzt werden.
      this.model.email2 = trimToNull(this.model.email2);
      this.model.email3 = trimToNull(this.model.email3);

      const request = <BatchAuftragCreateRequest>{
        optionen: this.model,
        template: this.template.model,
        sprache: this.translate.currentLang
      };
      const subscription = this.batchAuftragService.createDatenExportAuftrag(request).subscribe({
        next: (auftragsNummer) => {
          this.capAlertService.success(
            this.translate.instant('AuftragMitNummerErfolgreichErstellt', {
              auftragsNummer: auftragsNummer
            }),
            undefined,
            true
          );
          this.router.navigate(['..'], { relativeTo: this.route });
        },
        error: (error) => {
          if (!this.resolverValidationExceptionHandler.errorHandled(error)) {
            throw error;
          }
        }
      });
      subscription.add(() => this.enableSaveButton());
      this.subscriptions.push(subscription);
    }
  }

  private disableSaveButton() {
    this.saveRequestRunning = true;
  }

  private enableSaveButton() {
    this.saveRequestRunning = false;
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }
}
